import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputAdornment,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/formatters';
import { WatchlistEntry } from '../types';
import { PositionService } from '../services/positionService';
import { PortfolioService } from '../services/portfolioService';

interface TradeExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  watchlistEntry: WatchlistEntry | null;
  portfolioId: string;
  availableFunds: number;
  onTradeExecuted: () => void;
}

type OrderType = 'market' | 'limit';
type TradeStatus = 'pending' | 'executed';

export default function TradeExecutionDialog({
  open,
  onClose,
  watchlistEntry,
  portfolioId,
  availableFunds,
  onTradeExecuted,
}: TradeExecutionDialogProps) {
  const [orderType, setOrderType] = useState<OrderType>('market');
  const [tradeStatus, setTradeStatus] = useState<TradeStatus>('pending');
  const [quantity, setQuantity] = useState<number>(0);
  const [executionPrice, setExecutionPrice] = useState<string>('');
  const [executionDate, setExecutionDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd'T'HH:mm")
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Calculate suggested quantity based on 25% of available funds
  useEffect(() => {
    if (watchlistEntry && availableFunds > 0) {
      const targetPositionSize = availableFunds * 0.25; // 25% of available funds
      const suggestedQuantity = Math.floor(targetPositionSize / watchlistEntry.currentPrice);
      setQuantity(suggestedQuantity);
      setExecutionPrice(watchlistEntry.currentPrice.toFixed(2));
    }
  }, [watchlistEntry, availableFunds]);

  const handleExecuteTrade = async () => {
    if (!watchlistEntry) return;

    setError(null);
    setLoading(true);

    try {
      const price = parseFloat(executionPrice);
      const totalCost = price * quantity;

      if (totalCost > availableFunds) {
        throw new Error('Insufficient funds for this trade');
      }

      if (!watchlistEntry.pivotPrice) {
        throw new Error('Please set a pivot price before executing trades');
      }

      // Calculate stop loss from entry price (-5%) and use target from watchlist
      const stopLoss = price * 0.95; // -5% from entry price
      const targetPrice = watchlistEntry.targetPrice || price * 1.1; // Use watchlist target or default +10% from entry

      // Determine position status based on order type and trade status
      const positionStatus = orderType === 'limit' && tradeStatus === 'pending' ? 'pending' : 'open';

      // Parse execution date if provided (for executed trades)
      const executionDateObj = (orderType === 'market' || tradeStatus === 'executed') && executionDate
        ? new Date(executionDate)
        : undefined;

      // Open the position (or create pending position)
      await PositionService.openPosition(
        portfolioId,
        watchlistEntry.symbol,
        quantity,
        price,
        stopLoss,
        targetPrice,
        orderType,
        positionStatus,
        executionDateObj
      );

      // Update portfolio available funds
      // For pending orders, we still deduct the funds as they are allocated
      // First get the portfolio to calculate correctly
      const portfolio = await PortfolioService.getPortfolio(portfolioId);
      if (portfolio) {
        // Get all open positions to calculate correct available funds
        const openPositions = await PositionService.getOpenPositions(portfolioId);
        const openPositionsCost = openPositions.reduce(
          (sum, p) => sum + (p.entryPrice * p.quantity),
          0
        );
        
        // Calculate available funds: initial fund - open positions cost + realized losses
        const realizedLosses = Math.min(0, portfolio.totalEarnings || 0);
        const newAvailableFunds = portfolio.initialFundValue - openPositionsCost + realizedLosses;
        
        await PortfolioService.updateAvailableFunds(portfolioId, newAvailableFunds);
      }

      onTradeExecuted();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to execute trade');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOrderType('market');
    setTradeStatus('pending');
    setQuantity(0);
    setExecutionPrice('');
    setExecutionDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setError(null);
    onClose();
  };

  if (!watchlistEntry) return null;

  const totalCost = parseFloat(executionPrice || '0') * quantity;
  const percentOfFunds = availableFunds > 0 ? (totalCost / availableFunds) * 100 : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Execute Trade - {watchlistEntry.symbol}
        <Typography variant="body2" color="textSecondary">
          {watchlistEntry.companyName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip 
              label={`Current: ${formatCurrency(watchlistEntry.currentPrice)}`} 
              color="primary" 
            />
            {watchlistEntry.pivotPrice && (
              <Chip 
                label={`Pivot: ${formatCurrency(watchlistEntry.pivotPrice)}`} 
                color="secondary" 
              />
            )}
            <Chip 
              label={`Available: ${formatCurrency(availableFunds)}`} 
              color="info" 
            />
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Order Type</FormLabel>
          <RadioGroup
            row
            value={orderType}
            onChange={(e) => setOrderType(e.target.value as OrderType)}
          >
            <FormControlLabel 
              value="market" 
              control={<Radio />} 
              label="Market Order (Execute Now)" 
            />
            <FormControlLabel 
              value="limit" 
              control={<Radio />} 
              label="Limit Order (Pending)" 
            />
          </RadioGroup>
        </FormControl>

        {orderType === 'limit' && (
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Trade Status</FormLabel>
            <RadioGroup
              row
              value={tradeStatus}
              onChange={(e) => setTradeStatus(e.target.value as TradeStatus)}
            >
              <FormControlLabel 
                value="pending" 
                control={<Radio />} 
                label="Still Pending" 
              />
              <FormControlLabel 
                value="executed" 
                control={<Radio />} 
                label="Executed" 
              />
            </RadioGroup>
          </FormControl>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
          <TextField
            label="Quantity"
            type="number"
            fullWidth
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
            InputProps={{
              inputProps: { min: 1 }
            }}
          />
          <TextField
            label={orderType === 'market' ? 'Execution Price' : 'Limit Price'}
            type="number"
            fullWidth
            value={executionPrice}
            onChange={(e) => setExecutionPrice(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { step: 0.01 }
            }}
          />
        </Box>

        {(orderType === 'market' || tradeStatus === 'executed') && (
          <TextField
            label="Execution Date/Time"
            type="datetime-local"
            fullWidth
            value={executionDate}
            onChange={(e) => setExecutionDate(e.target.value)}
            sx={{ mb: 3 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}

        <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Trade Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Total Cost:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {formatCurrency(totalCost)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              % of Available Funds:
            </Typography>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              color={percentOfFunds > 25 ? 'error' : 'success'}
            >
              {percentOfFunds.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Remaining Funds:
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {formatCurrency(availableFunds - totalCost)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleExecuteTrade}
          disabled={
            loading || 
            quantity <= 0 || 
            !executionPrice || 
            totalCost > availableFunds
          }
        >
          {orderType === 'market' ? 'Execute Trade' : 
           tradeStatus === 'pending' ? 'Save Pending Order' : 'Record Executed Trade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}